import React, { useState, useRef, useEffect } from 'react';
import { getKeyboardLayout } from '../utils/keyboardLayouts';
import './VirtualKeyboard.css';

const VirtualKeyboard = ({ language, matrice, onClose, onCharacterClick }) => {
  const keyboardLayout = getKeyboardLayout(language);
  const [position, setPosition] = useState({ x: window.innerWidth / 2 - 400, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [shiftPressed, setShiftPressed] = useState(false);
  const keyboardRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleMouseDown = (e) => {
    if (e.target.closest('.keyboard-header') && !e.target.closest('.keyboard-close')) {
      const rect = keyboardRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsDragging(true);
    }
  };

  const getCharacterColor = (char) => {
    if (!char) return null;
    
    const charUpper = char.toUpperCase();
    const charLower = char.toLowerCase();
    
    // Pour les caractères spéciaux (accents)
    if (matrice?.caracteres_speciaux_couleurs && matrice.caracteres_speciaux_couleurs[char]) {
      return matrice.caracteres_speciaux_couleurs[char];
    }
    
    // Pour les lettres, chercher d'abord en majuscules car les alphabets sont en majuscules
    if (matrice?.lettres_couleurs) {
      const color = matrice.lettres_couleurs[charUpper] || matrice.lettres_couleurs[charLower];
      if (color) return color;
    }
    
    // Pour les chiffres
    if (matrice?.chiffres_couleurs && matrice.chiffres_couleurs[char]) {
      return matrice.chiffres_couleurs[char];
    }
    
    return null;
  };

  const handleKeyClick = (keyObj) => {
    // Si c'est une touche spéciale (espace), utiliser directement la valeur key
    const charToInsert = shiftPressed && keyObj.shift ? keyObj.shift : keyObj.key;
    
    if (onCharacterClick) {
      onCharacterClick(charToInsert);
    }
  };

  return (
    <div className="keyboard-modal-overlay">
      <div 
        ref={keyboardRef}
        className="keyboard-modal draggable"
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: 'none'
        }}
        onMouseDown={handleMouseDown}
      >
        <div className="keyboard-header draggable-handle">
          <h3>{keyboardLayout.name}</h3>
          <button className="keyboard-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        
        <div className="keyboard-content">
          <div className="keyboard-shift-toggle">
            <label>
              <input 
                type="checkbox" 
                checked={shiftPressed} 
                onChange={(e) => setShiftPressed(e.target.checked)} 
              />
              <span>Shift / Majuscules</span>
            </label>
          </div>
          
          {keyboardLayout.rows.map((row, rowIndex) => (
            <div key={rowIndex} className={`keyboard-row ${rowIndex === 4 ? 'keyboard-row-space' : ''}`}>
              {row.map((keyObj, keyIndex) => {
                const charToCheck = shiftPressed && keyObj.shift ? keyObj.shift : keyObj.key;
                const color = getCharacterColor(charToCheck);
                const isSpaceBar = keyObj.special === 'space';
                
                return (
                  <button
                    key={`${rowIndex}-${keyIndex}`}
                    className={`keyboard-key ${isSpaceBar ? 'keyboard-key-space' : ''} ${shiftPressed ? 'shift-active' : ''}`}
                    style={{
                      backgroundColor: color || '#f3f4f6',
                      color: color ? getContrastColor(color) : '#333',
                      border: color ? '2px solid #ddd' : '2px solid #e5e7eb'
                    }}
                    onClick={() => handleKeyClick(keyObj)}
                    title={color ? `${keyObj.display}: ${color}` : keyObj.display}
                  >
                    {shiftPressed && keyObj.shift ? (
                      <span className="key-shift">{keyObj.shift}</span>
                    ) : (
                      <span className="key-normal">{keyObj.display}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

function getContrastColor(hexColor) {
  if (!hexColor || hexColor === '#ffffff') return '#000000';
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#000000' : '#ffffff';
}

export default VirtualKeyboard;

